import React, {useEffect, useMemo, useState} from "react"
import "@styles/react/libs/editor/editor.scss"
import "@styles/base/plugins/forms/form-quill-editor.scss"
import {Link, useHistory, useParams} from "react-router-dom"
import {
    Card,
    CardBody,
    CardTitle,
    Row,
    Col,
    CardText,
    Button,
    Spinner,
    FormGroup,
    Label,
    Input,
    Form,
    CustomInput,
    Badge,
    Alert
} from "reactstrap"
import {useDispatch, useSelector} from "react-redux"
import {
    campaignView,
    applyOnCampaign,
    applyOnCampaignReset,
    ambassadorLinks
} from "@store/actions/RDActions"
import {useFormik} from "formik"
import * as Yup from "yup"
import classnames from "classnames"
import BlankLogo from "@src/assets/images/avatars/Blank-logo.jpg"
import Dots from "../../../assets/images/dots.svg"
import HomeIcon from "@src/assets/images/home.svg"
import BgImg from "@src/assets/images/bg.svg"
import Swal from 'sweetalert2'
import withReactContent from 'sweetalert2-react-content'
import ArrowLeft from "@src/assets/images/arrowLeft.svg"
import {CheckCircle, AlertCircle, Info, Clock, Users} from "react-feather"
import axios from "axios"

const API_URL = process.env.REACT_APP_BASEURL

// ─── Reusable: Already Applied Banner ─────────────────────────────────────────
const AlreadyAppliedBanner = () => (
    <Alert color="success" className="mb-2 p-2 border-0 rounded" style={{ backgroundColor: '#ebfbf3', borderLeft: '5px solid #28c76f' }}>
        <div className="d-flex align-items-start">
            <CheckCircle size={22} className="text-success mr-1 mt-25 flex-shrink-0" />
            <div>
                <h5 className="mt-45 font-weight-bold text-success mb-50">Applied Successfully</h5>
                <p className="m-0 text-dark" style={{ fontSize: '12px', lineHeight: '1.5' }}>
                    You have already submitted your application for this outreach. 
                    Our team is currently reviewing your submission and will contact you directly with updates.
                </p>
            </div>
        </div>
    </Alert>
)

// ─── Reusable: Spots Left Badge ────────────────────────────────────────────────
const SpotsLeftBadge = ({spotsLeft, isEnded = false}) => {
    if (isEnded) {
        return (
            <Badge color="danger" pill className="px-1 py-50" style={{fontSize: '12px'}}>
                Ended
            </Badge>
        )
    }
    if (!spotsLeft && spotsLeft !== 0) return null
    if (spotsLeft === 0) return (
        <Badge color="danger" pill className="px-1 py-50" style={{fontSize: '12px'}}>
            Full
        </Badge>
    )
    const color = spotsLeft <= 5 ? 'warning' : 'success'
    return (
        <Badge color={color} pill className="px-1 py-50" style={{fontSize: '12px'}}>
            {spotsLeft} {spotsLeft === 1 ? 'spot' : 'spots'} left
        </Badge>
    )
}

// ─── Brand Avatar Helpers & Themes ───────────────────────────────────────────
const PASTEL_THEMES = [
    { bg: '#dbeafe', color: '#1d4ed8' }, // Blue
    { bg: '#fce7f3', color: '#9d174d' }, // Pink
    { bg: '#ccfbf1', color: '#0f766e' }, // Mint
    { bg: '#fef3c7', color: '#b45309' }, // Amber
    { bg: '#ede9fe', color: '#6d28d9' }, // Purple
    { bg: '#e0e7ff', color: '#4338ca' }, // Indigo
    { bg: '#fee2e2', color: '#b91c1c' }, // Rose
    { bg: '#f0fdf4', color: '#15803d' }  // Green
]

const getBrandInitials = (name = '') => {
    if (!name) return 'B'
    // Normalize diacritics / accents (e.g. é -> e) and filter words for pure English alphabet letters
    const normalized = name.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    const wordsWithAlphabets = normalized
        .split(/\s+/)
        .map(word => word.replace(/[^a-zA-Z]/g, ''))
        .filter(cleanWord => cleanWord.length > 0)

    if (wordsWithAlphabets.length >= 2) {
        return (wordsWithAlphabets[0][0] + wordsWithAlphabets[1][0]).toUpperCase()
    }
    if (wordsWithAlphabets.length === 1) {
        return wordsWithAlphabets[0][0].toUpperCase()
    }
    const anyLetters = normalized.replace(/[^a-zA-Z]/g, '')
    if (anyLetters.length > 0) {
        return anyLetters[0].toUpperCase()
    }
    return 'B'
}

const getBrandAvatarStyle = (name = '') => {
    const lower = (name || '').toLowerCase()
    if (lower.includes('orgain')) return { backgroundColor: '#dbeafe', color: '#1d4ed8' }
    if (lower.includes('needed')) return { backgroundColor: '#fce7f3', color: '#9d174d' }
    if (lower.includes('intoleran')) return { backgroundColor: '#ccfbf1', color: '#0f766e' }

    let hash = 0
    for (let i = 0; i < name.length; i++) {
        hash = name.charCodeAt(i) + ((hash << 5) - hash)
    }
    const theme = PASTEL_THEMES[Math.abs(hash) % PASTEL_THEMES.length]
    return {
        backgroundColor: theme.bg,
        color: theme.color
    }
}

// ─── Reusable: Brand Avatar ──────────────────────────────────────────────────
const BrandAvatar = ({brandName, logoUrl}) => {
    const [imgError, setImgError] = useState(false)
    const initials = getBrandInitials(brandName)
    const avatarStyle = getBrandAvatarStyle(brandName)

    const hasRealLogo = Boolean(
        logoUrl &&
        typeof logoUrl === 'string' &&
        !logoUrl.includes('Blank-logo') &&
        !logoUrl.includes('avatar-blank')
    )

    if (hasRealLogo && !imgError) {
        return (
            <div className="recommended-opp-avatar-container">
                <img
                    src={logoUrl}
                    alt={brandName || 'Brand'}
                    className="recommended-opp-avatar-img"
                    onError={() => setImgError(true)}
                />
            </div>
        )
    }

    return (
        <div
            className="recommended-opp-avatar-container recommended-opp-avatar-fallback"
            style={avatarStyle}
        >
            {initials}
        </div>
    )
}

// ─── Reusable: Recommended Opportunity Card ──────────────────────────────────
const RecommendedOpportunityCard = ({opp}) => {
    const history = useHistory()
    const brandName = opp.brand_name || opp.getUser?.getBrandProfile?.brand_name || opp.getUser?.name || 'Brand'
    const logoUrl = opp.brand_logo || opp.brand_img || opp.getUser?.getBrandProfile?.brand_logo_url || opp.getUser?.getBrandProfile?.brand_logo || null
    const subtitle = opp.campaign_type || (opp.opportunity_for ? `${opp.opportunity_for.charAt(0).toUpperCase() + opp.opportunity_for.slice(1)} samples` : 'Free samples')
    const specialityText = opp.speciality_summary || (Array.isArray(opp.specialities) && opp.specialities.length > 0 ? opp.specialities.slice(0, 2).join(' · ') : 'All specialities')

    const handleView = (e) => {
        e.preventDefault()
        window.scrollTo({top: 0, behavior: 'smooth'})
        history.push(`/rd/apply-on-campaign/${opp.id}`)
    }

    return (
        <div className="recommended-opp-card">
            <div>
                {/* Top Row: Brand Info + Status Badge */}
                <div className="d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center overflow-hidden pr-1">
                        <BrandAvatar brandName={brandName} logoUrl={logoUrl} />
                        <div className="ml-1 d-flex flex-column justify-content-center overflow-hidden">
                            <span className="recommended-opp-brand-name text-truncate" title={brandName}>
                                {brandName}
                            </span>
                            <span className="recommended-opp-subtitle text-truncate">
                                {subtitle}
                            </span>
                        </div>
                    </div>
                    <div className="flex-shrink-0">
                        {opp.is_full || opp.spots_left === 0 ? (
                            <span className="recommended-opp-badge-full">Filled</span>
                        ) : opp.spots_left !== null && opp.spots_left !== undefined ? (
                            <span className="recommended-opp-badge-spots">
                                <Clock size={12} className="mr-50" />
                                {opp.spots_left} {opp.spots_left === 1 ? 'spot left' : 'spots left'}
                            </span>
                        ) : (
                            <span className="recommended-opp-badge-open">Open</span>
                        )}
                    </div>
                </div>

                {/* Middle Row: Campaign Title */}
                <h6 className="recommended-opp-title" title={opp.campaign_title}>
                    {opp.campaign_title}
                </h6>
            </div>

            {/* Bottom Row: Audience / Specialities + View Button */}
            <div className="d-flex align-items-center justify-content-between pt-50">
                <div className="d-flex align-items-center text-muted overflow-hidden mr-1">
                    <Users size={14} className="mr-50 flex-shrink-0 text-muted" />
                    <span className="recommended-opp-specialities text-truncate" title={specialityText}>
                        {specialityText}
                    </span>
                </div>
                <Link
                    to={`/rd/apply-on-campaign/${opp.id}`}
                    onClick={handleView}
                    className="btn recommended-opp-view-btn flex-shrink-0"
                >
                    View
                </Link>
            </div>
        </div>
    )
}

// ─── Reusable: Recommended Opportunities Section ─────────────────────────────
const RecommendedOpportunitiesSection = ({opportunities = [], className = ''}) => {
    // Only recommend outreach in which user has not applied
    const validOpportunities = (opportunities || []).filter(opp => !opp.is_applied)
    if (validOpportunities.length === 0) return null

    return (
        <div className={`recommended-opportunities-section ${className}`}>
            <div className="recommended-opp-header">
                <h4 className="recommended-opp-heading">More opportunities for you</h4>
                <Link to="/dashboard/rd/campaigns" className="recommended-opp-browse-all">
                    Browse all
                </Link>
            </div>
            <Row>
                {validOpportunities.map((opp) => (
                    <Col md="6" key={opp.id} className="mb-2">
                        <RecommendedOpportunityCard opp={opp} />
                    </Col>
                ))}
            </Row>
        </div>
    )
}

// ─── Reusable: Post Full / Ended Page ──────────────────────────────────────────
const PostFullPage = ({campaignTitle, brandName, moreOpportunities = [], isDisabled = false}) => (
    <div className="py-4 px-1 mx-auto" style={{maxWidth: '1080px'}}>
        <div className="text-center mb-3">
            <AlertCircle size={56} className="text-warning mb-2" />
            <h3 className="font-weight-bold mb-50">{isDisabled ? "Ended" : "This post has filled."}</h3>
            <p className="text-muted mb-0">
                {isDisabled ? (
                    <>This outreach is no longer accepting responses.</>
                ) : (
                    <><strong>{campaignTitle}</strong> {brandName ? `by ${brandName}` : ''} is no longer accepting applications.</>
                )}
            </p>
        </div>
        {moreOpportunities.length > 0 && (
            <div className="mt-3">
                <RecommendedOpportunitiesSection opportunities={moreOpportunities} />
            </div>
        )}
    </div>
)

// ─── Main Component ────────────────────────────────────────────────────────────
const ApplyOnCampaign = () => {
    const history = useHistory()
    const sweetAlert = withReactContent(Swal)
    const dispatch = useDispatch()
    const {id, mode} = useParams()

    // Fallback state for when API returns 422 (post is full / deactivated)
    const [statusFallback, setStatusFallback] = useState(null)
    const [statusFallbackLoading, setStatusFallbackLoading] = useState(false)

    const {
        campaignViewLoading,
        campaignViewData,
        campaignViewError,
        applyOnCampaignLoading,
        applyOnCampaignData,
        applyOnCampaignError
    } = useSelector((store) => store.rdReducer)

    const {data} = useMemo(() => {
        return {data: campaignViewData?.data ?? null}
    }, [campaignViewData])

    const isFormDisabled = applyOnCampaignLoading || Boolean(data?.is_expired) || mode === 'view' || (data?.is_applied && mode !== 'update') || Boolean(data?.is_full) || Boolean(data?.is_disabled) || Boolean(data?.status === 'disabled')

    const getValidationSchema = () => {
        const baseSchema = {}
        if (data?.sample_provided_for_participants === 1 || data?.campaign_type?.id === 2) {
            baseSchema.first_name = Yup.string().trim().required("This field is required.")
            baseSchema.last_name = Yup.string().trim().required("This field is required.")
            baseSchema.street_address_1 = Yup.string().trim().required("This field is required.")
            baseSchema.city = Yup.string().trim().required("This field is required.")
            baseSchema.state = Yup.string().trim().required("This field is required.")
            baseSchema.zipcode = Yup.string().trim().required("This field is required.")
        }
        if (data?.campaign_questions?.length > 0) {
            baseSchema.campaign_form_questions = Yup.array().of(Yup.object().shape({
                    id: Yup.number().required(),
                    campaign_id: Yup.number().required(),
                    question: Yup.string().required(),
                    type: Yup.string().required(),
                    campaign_question_options: Yup.array().of(
                        Yup.object().shape({
                            id: Yup.number().required(),
                            question_id: Yup.number().required(),
                            answer: Yup.string().required()
                        })
                    ),
                    user_ans: Yup.string().required('This field is required')
                })
            ).required('At least one question is required').min(1, 'At least one question is required')
        }
        return Yup.object().shape(baseSchema)
    }

    const form = useFormik({
        initialValues: {
            first_name: "",
            last_name: "",
            street_address_1: "",
            street_address_2: "",
            city: "",
            state: "",
            zipcode: "",
            campaign_form_questions: []
        },
        validationSchema: getValidationSchema(),
        onSubmit: (values) => {
            const result = new FormData()
            const {
                campaign_form_questions,
                first_name,
                last_name,
                street_address_1,
                street_address_2,
                city,
                state,
                zipcode
            } = values
            result.append('campaign_id', id)
            result.append('first_name', first_name)
            result.append('last_name', last_name)
            result.append('street_address_1', street_address_1)
            result.append('street_address_2', street_address_2)
            result.append('city', city)
            result.append('state', state)
            result.append('zipcode', zipcode)
            if (campaign_form_questions) {
                campaign_form_questions.forEach((item) => {
                    if (item.type === "textField") {
                        result.append(`text_answers[${item?.id}]`, item.user_ans)
                    } else if (item.type === "multiple") {
                        result.append(`answers_selected[]`, item.user_ans)
                    }
                })
            }
            sweetAlert.fire({
                title: mode === 'update' ? 'Update Application?' : 'Apply?',
                icon: 'warning',
                html: mode === 'update' ? 'Do you want to save updates to your application?' : 'Do you really want to apply?',
                confirmButtonText: mode === 'update' ? 'Update' : 'Yes',
                allowOutsideClick: false,
                showCancelButton: true
            }).then((e) => {
                if (e.isConfirmed) {
                    dispatch(applyOnCampaign(result))
                }
            })
        }
    })

    useEffect(() => {
        if (data?.campaign_questions) {
            const dataFormatted = data?.campaign_questions?.map((item) => {
                if (item.type === 'textField') {
                    return {...item, user_ans: item?.get_user_ans?.answer_text ?? ''}
                } else if (item.type === 'multiple') {
                    return {...item, user_ans: item?.get_user_ans?.answer_id ?? ''}
                } else {
                    return {...item, user_ans: ''}
                }
            })
            form.setFieldValue('campaign_form_questions', dataFormatted)
        }
        if (data?.get_mailing_address) {
            form.setFieldValue('first_name', data?.get_mailing_address.first_name ?? '')
            form.setFieldValue('last_name', data?.get_mailing_address.last_name ?? '')
            form.setFieldValue('street_address_1', data?.get_mailing_address.street_address_1 ?? '')
            form.setFieldValue('street_address_2', data?.get_mailing_address.street_address_2 ?? '')
            form.setFieldValue('city', data?.get_mailing_address.city ?? '')
            form.setFieldValue('state', data?.get_mailing_address.state ?? '')
            form.setFieldValue('zipcode', data?.get_mailing_address.zipcode ?? '')
        }
    }, [data])

    useEffect(() => {
        if (applyOnCampaignData?.status) {
            sweetAlert.fire({
                title: mode === 'update' ? 'Updated!' : 'Congratulations',
                icon: 'success',
                html: applyOnCampaignData?.msg || (mode === 'update' ? 'Application updated successfully.' : 'Application submitted successfully.')
            })
            dispatch(applyOnCampaignReset())
            history.push('/dashboard/rd/campaigns')
        } else if (applyOnCampaignData && applyOnCampaignData.status === false) {
            sweetAlert.fire({
                title: 'Error',
                icon: 'error',
                html: applyOnCampaignData?.msg || 'This outreach post has expired and is no longer accepting applications.'
            })
            dispatch(applyOnCampaignReset())
        }

        if (applyOnCampaignError) {
            const errorMsg = applyOnCampaignError?.response?.data?.msg
                || applyOnCampaignError?.response?.data?.message
                || applyOnCampaignError?.response?.data?.error
                || applyOnCampaignError?.message
                || 'Something went wrong. Please try again.'
            sweetAlert.fire({
                title: 'Error',
                icon: 'error',
                html: errorMsg
            })
            dispatch(applyOnCampaignReset())
        }
    }, [applyOnCampaignData, applyOnCampaignError])

    useEffect(() => {
        form.resetForm()
        setStatusFallback(null)
        setStatusFallbackLoading(false)
        dispatch(campaignView(`?campaign_id=${id}`))
        window.scrollTo({top: 0, behavior: 'smooth'})
    }, [id])

    // When the API fails (e.g. campaign deactivated/not found), fetch public status instead
    useEffect(() => {
        if (campaignViewError && !statusFallbackLoading) {
            setStatusFallbackLoading(true)
            axios.get(`${(API_URL || '').replace(/\/$/, '')}/rd/campaign-status/${id}`)
                .then(res => setStatusFallback(res.data))
                .catch(() => setStatusFallback({found: false}))
                .finally(() => setStatusFallbackLoading(false))
        }
    }, [campaignViewError, id])

    const formatExternalUrl = (url) => {
        if (!url) return ''
        const trimmed = url.trim()
        return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`
    }

    const handleAmbassadorLink = () => {
        const targetUrl = formatExternalUrl(data?.platform_external_link)
        if (targetUrl) {
            dispatch(ambassadorLinks({
                campaign_id: id,
                link: targetUrl
            }))
        }
    }

    // ── Loading state ──────────────────────────────────────────────────────────
    if (campaignViewLoading || statusFallbackLoading) {
        return (
            <div className='bg-primary text-white text-center p-1'>
                <Spinner style={{width: '18px', height: '18px'}}/> Please wait...
            </div>
        )
    }

    // ── Fallback: API failed → show public status (post full / not found) ──────
    if (campaignViewError || (!campaignViewLoading && !data)) {
        if (statusFallbackLoading) {
            return (
                <div className='bg-primary text-white text-center p-1'>
                    <Spinner style={{width: '18px', height: '18px'}}/> Please wait...
                </div>
            )
        }
        if (statusFallback?.found === false) {
            return (
                <div className='font-weight-bold text-center py-4'>
                    <AlertCircle size={48} className="text-danger mb-1" />
                    <div className='mb-1'>Sorry! Outreach not found.</div>
                    <Link className='text-primary' to='/dashboard/rd/campaigns'>Go Back</Link>
                </div>
            )
        }
        if (statusFallback?.is_full || statusFallback?.is_disabled || statusFallback?.found) {
            const isEnded = Boolean(statusFallback?.is_disabled || statusFallback?.status === 'disabled' || (statusFallback?.found && !statusFallback?.is_full))
            return (
                <PostFullPage
                    campaignTitle={statusFallback.campaign_title || 'This outreach post'}
                    brandName={statusFallback.brand_name}
                    moreOpportunities={statusFallback.more_opportunities ?? []}
                    isDisabled={isEnded}
                />
            )
        }
        return (
            <div className='font-weight-bold text-center py-4'>
                <div className='mb-1'>Sorry! Outreach not found.</div>
                <Link className='text-primary' to='/dashboard/rd/campaigns'>Go Back</Link>
            </div>
        )
    }

    // ── Main render (campaign loaded) ──────────────────────────────────────────
    return (
        <>
            <div className="d-flex align-items-center justify-content-between pb-2">
                 <div className="d-flex align-items-center pb-1">
                    <Link to='/dashboard/rd/campaigns' className='btn p-0'>
                        <img className="pr-1" src={ArrowLeft} alt="Arrow Left"/>
                    </Link>
                    <h2 className="m-0 font-inter inter-medium" style={{color: '#000'}}>
                        Apply
                    </h2>
                </div>
                <div className="d-flex align-items-center">
                    <Link to='/dashboard/rd' className='btn p-0'>
                        <img src={HomeIcon} alt="Home"/>
                    </Link>
                    <div className="px-1">/</div>
                    <Link to='/dashboard/rd/campaigns' className='btn p-0'>
                        <div>Brand Outreach Posts</div>
                    </Link>
                </div>
            </div>

            {/* ── Already Applied Banner (only when outreach is active) ── */}
            {data?.is_applied && !data?.is_disabled && data?.status !== 'disabled' && mode !== 'update' && <AlreadyAppliedBanner />}

            {/* ── Update Application Info Banner ─────────────────────────── */}
            {mode === 'update' && (
                <Alert color="info" className="mb-2 p-2 border-0 rounded" style={{ backgroundColor: '#f0f8ff', borderLeft: '5px solid #00cfe8' }}>
                    <div className="d-flex align-items-start">
                        <Info size={22} className="text-info mr-1 mt-25 flex-shrink-0" />
                        <div>
                            <h5 className="mt-45 font-weight-bold text-info mb-50">Update Your Application</h5>
                            <p className="m-0 text-dark" style={{ fontSize: '12px', lineHeight: '1.5' }}>
                                This outreach was updated by the brand. Please review your answers below, complete any new information requested, and click <strong>Update Application</strong>.
                            </p>
                        </div>
                    </div>
                </Alert>
            )}

            {/* ── Post Disabled / Ended Banner ─────────────────────────────── */}
            {(data?.status === 'disabled' || data?.is_disabled) && (
                <Alert color="warning" className="mb-2 p-2 border-0 rounded" style={{ backgroundColor: '#fff8e6', borderLeft: '5px solid #ff9f43' }}>
                    <div className="d-flex align-items-center">
                        <AlertCircle size={22} className="text-warning mr-1 flex-shrink-0" />
                        <div>
                            <h5 className="m-0 font-weight-bold text-warning">
                                This outreach is no longer accepting responses
                            </h5>
                        </div>
                    </div>
                </Alert>
            )}

            {/* ── Post Expired Banner ─────────────────────────────────────── */}
            {data?.is_expired && !data?.is_applied && data?.status !== 'disabled' && (
                <Alert color="danger" className="mb-2 p-2 border-0 rounded" style={{ backgroundColor: '#fcebeb', borderLeft: '5px solid #ea5455' }}>
                    <div className="d-flex align-items-start">
                        <AlertCircle size={22} className="text-danger mr-1 mt-25 flex-shrink-0" />
                        <div>
                            <h5 className="mt-45 font-weight-bold text-danger mb-50">Outreach Expired</h5>
                            <p className="m-0 text-dark" style={{ fontSize: '12px', lineHeight: '1.5' }}>
                                This outreach post has expired and is no longer accepting new applications.
                            </p>
                        </div>
                    </div>
                </Alert>
            )}

            {/* ── Post Full Banner ───────────────────────────────────────── */}
            {data?.is_full && !data?.is_applied && !data?.is_expired && data?.status !== 'disabled' && !data?.is_disabled && (
                <Alert color="warning" className="mb-2 p-2 border-0 rounded" style={{ backgroundColor: '#fff8e6', borderLeft: '5px solid #ff9f43' }}>
                    <div className="d-flex align-items-start">
                        <AlertCircle size={22} className="text-warning mr-1 mt-25 flex-shrink-0" />
                        <div>
                            <h5 className="mt-45 font-weight-bold text-warning mb-50">Outreach Full</h5>
                            <p className="m-0 text-dark" style={{ fontSize: '12px', lineHeight: '1.5' }}>
                                This outreach post has been filled and is no longer accepting new applications.
                            </p>
                        </div>
                    </div>
                </Alert>
            )}

            <Card className="p-2 rdViewCard">
                <Row className="position-relative">
                    <Col md={12} className="position-relative">
                        <img src={data?.banner_image || BgImg} alt="Banner" className="img-fluid rounded w-100 bgImg"/>
                    </Col>
                </Row>
                <Row className="mt-3 position-relative">
                    <Col md={2} className="text-center position-relative">
                        <img src={`${data?.get_user?.get_brand_profile?.brand_logo_url || BlankLogo}`} alt="Brand Logo" className="rounded-circle img-fluid profileImg img-fit-contain"/>
                    </Col>
                    <Col md={10}>
                        <div className="d-flex align-items-start justify-content-between flex-wrap">
                            <div className="name-description">
                                <CardTitle className="name mb-25" tag="h4">
                                    {data?.campaign_title}
                                </CardTitle>
                                <CardText className="description">
                                    {data?.campaign_type?.title}
                                </CardText>
                            </div>
                            <div className="mt-50 mt-md-0 flex-shrink-0">
                                <SpotsLeftBadge spotsLeft={data?.spots_left} isEnded={Boolean(data?.status === 'disabled' || data?.is_disabled)} />
                            </div>
                        </div>
                    </Col>
                </Row>
                <CardBody>
                    <h5>Description</h5>
                    <div>
                        <div dangerouslySetInnerHTML={{__html: data?.campaign_details}}/>
                    </div>
                    <h6 className="mt-3">Application Deadline</h6>
                    <CardText>{data?.expire_after ? data?.expire_after : 'No specific end date'}</CardText>
                </CardBody>
            </Card>

            {/* ── If post is full or disabled: show alternative opportunities ─────────── */}
            {(data?.is_full || data?.is_disabled || data?.status === 'disabled') && data?.more_opportunities?.length > 0 && (
                <div className="mt-2 mb-3">
                    <RecommendedOpportunitiesSection opportunities={data.more_opportunities} />
                </div>
            )}

            {/* ── Application Form (only shown when post is active and open) ─────────────── */}
            {!data?.is_full && !data?.is_disabled && data?.status !== 'disabled' && (
                <Form onSubmit={form.handleSubmit}>
                    {data?.campaign_type?.id === 3 && (
                        <Card>
                            <CardBody>
                                <h5 className="f_size bold_weight text-dark font-inter inter-medium ml-1 mb-2">
                                    RD Requirements
                                </h5>
                                <h6 className="f_size bold_weight font-inter inter-regular ml-1 text-line-height"
                                    dangerouslySetInnerHTML={{
                                        __html: data?.rd_requirements?.replace(/\r\n/g, "<br />")
                                    }}
                                ></h6>
                            </CardBody>
                            <CardBody>
                                <h5 className="f_size bold_weight text-dark font-inter inter-medium ml-1 mb-2">
                                    Partnership Requirements & Guidelines
                                </h5>
                                <h6 className="f_size bold_weight font-inter inter-regular ml-1 text-line-height"
                                    dangerouslySetInnerHTML={{
                                        __html: data?.partnership_requirements_and_guidelines?.replace(/\r\n/g, "<br />")
                                    }}
                                ></h6>
                            </CardBody>
                            <CardBody>
                                <h5 className="f_size bold_weight text-dark font-inter inter-medium ml-1 mb-2">
                                    Compensation
                                </h5>
                                <h6 className="f_size bold_weight font-inter inter-regular ml-1">
                                    {data?.budget_per_rd}
                                </h6>
                            </CardBody>
                        </Card>
                    )}

                    {data?.campaign_type?.id === 1 && data?.platform_external_link && (
                        <Card>
                            <CardBody>
                                <CardTitle className="name" tag="h4">
                                    Submission Form
                                </CardTitle>
                                <div>
                                    <strong>Sign-up link.</strong>
                                    <div>Please click on this link to sign up:</div>
                                    <a
                                        href={formatExternalUrl(data?.platform_external_link)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="btn btn-link p-0 text-underline"
                                        onClick={handleAmbassadorLink}
                                    >
                                        Sign up Here
                                    </a>
                                </div>
                            </CardBody>
                        </Card>
                    )}

                    {data?.campaign_questions?.length > 0 && (
                        <Card>
                            <CardBody>
                                <CardTitle className="name" tag="h4">
                                   Submission Form
                                </CardTitle>
                                {form.values.campaign_form_questions.map((field, index) => (
                                    <div key={`campaign-form-${index}`}>
                                        <div className="d-flex align-items-start mb-1">
                                            <img className="pt-25 pr-2" src={Dots} alt="icon"/>
                                            <div className="w-100">
                                                <FormGroup className="mb-0">
                                                    <div className={classnames({
                                                        'is-invalid': form.touched.campaign_form_questions?.[index]?.user_ans && form.errors.campaign_form_questions?.[index]?.user_ans,
                                                        'mb-50 font-inter inter-medium': true
                                                    })} dangerouslySetInnerHTML={{__html: field.question?.replace(/\r\n/g, "<br />")}}></div>
                                                    {field.type === 'multiple' && (
                                                        field?.campaign_question_options?.map((option, i) => (
                                                            <FormGroup check={true} key={`campaign-form-${index}-${i}`} className="d-flex align-items-center mb-50 p-0">
                                                                <CustomInput name={`option-${index}`}
                                                                       id={`option-${index}-${i}`}
                                                                       type="radio"
                                                                       value={option?.id}
                                                                       onChange={() => {
                                                                           form.setFieldValue(`campaign_form_questions[${index}].user_ans`, option?.id)
                                                                       }}
                                                                       checked={form.values.campaign_form_questions[index].user_ans === option?.id}
                                                                       className={classnames({
                                                                           'is-invalid': form.touched.campaign_form_questions?.[index]?.user_ans && form.errors.campaign_form_questions?.[index]?.user_ans
                                                                       })}
                                                                       disabled={isFormDisabled}
                                                                />
                                                                {" "}
                                                                <Label className={classnames({
                                                                    'is-invalid': form.touched.campaign_form_questions?.[index]?.user_ans && form.errors.campaign_form_questions?.[index]?.user_ans
                                                                })}>
                                                                    {option?.answer}
                                                                </Label>
                                                            </FormGroup>
                                                        ))
                                                    )}
                                                    {field.type !== 'multiple' && (
                                                        <FormGroup>
                                                            <Input placeholder="Your Answer..."
                                                                   id={`que-${index}`}
                                                                   type="textarea"
                                                                   rows={3}
                                                                   className={classnames({
                                                                       'is-invalid': form.touched.campaign_form_questions?.[index]?.user_ans && form.errors.campaign_form_questions?.[index]?.user_ans
                                                                   })}
                                                                   name={`campaign_form_questions[${index}].user_ans`}
                                                                   value={form.values.campaign_form_questions[index].user_ans}
                                                                   onChange={form.handleChange}
                                                                   onBlur={form.handleBlur}
                                                                   disabled={isFormDisabled}
                                                            />
                                                        </FormGroup>
                                                    )}
                                                </FormGroup>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </CardBody>
                        </Card>
                    )}

                    {((data?.sample_provided_for_participants === 1) || (data?.campaign_type?.id === 2)) && (
                        <Card>
                            <CardBody>
                                <CardTitle className="name text-dark" tag="h4">
                                    To receive a welcome package or samples, please share your mailing address.
                                </CardTitle>
                                <Row>
                                    <Col md="6">
                                        <FormGroup>
                                            <Label className={classnames('formlabel', {
                                                'is-invalid': form.touched.first_name && form.errors.first_name,
                                                'form-label': true
                                            })}
                                                   htmlFor='first_name'
                                            >
                                                First Name <span className="is-invalid ml-25">*</span>
                                            </Label>
                                            <Input placeholder="First Name"
                                                   type="text"
                                                   name='first_name'
                                                   id='first_name'
                                                   value={form.values.first_name}
                                                   onBlur={form.handleBlur}
                                                   onChange={form.handleChange}
                                                   className={classnames({'is-invalid': form.touched.first_name && form.errors.first_name})}
                                                   disabled={isFormDisabled}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md="6">
                                        <FormGroup>
                                            <Label className={classnames('formlabel', {
                                                'is-invalid': form.touched.last_name && form.errors.last_name,
                                                'form-label': true
                                            })}
                                                   htmlFor='last_name'
                                            >
                                                Last Name <span className="is-invalid ml-25">*</span>
                                            </Label>
                                            <Input placeholder="Last Name"
                                                   type="text"
                                                   name='last_name'
                                                   id='last_name'
                                                   value={form.values.last_name}
                                                   onBlur={form.handleBlur}
                                                   onChange={form.handleChange}
                                                   className={classnames({'is-invalid': form.touched.last_name && form.errors.last_name})}
                                                   disabled={isFormDisabled}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md="12">
                                        <FormGroup>
                                            <Label className={classnames('formlabel', {
                                                'is-invalid': form.touched.street_address_1 && form.errors.street_address_1,
                                                'form-label': true
                                            })}
                                                   htmlFor='street_address'
                                            >
                                                Street Address 1 <span className="is-invalid ml-25">*</span>
                                            </Label>
                                            <Input placeholder="Street Address 1"
                                                   type="text"
                                                   name='street_address_1'
                                                   id='street_address_1'
                                                   value={form.values.street_address_1}
                                                   onBlur={form.handleBlur}
                                                   onChange={form.handleChange}
                                                   className={classnames({'is-invalid': form.touched.street_address_1 && form.errors.street_address_1})}
                                                   disabled={isFormDisabled}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md="12">
                                        <FormGroup>
                                            <Label className={classnames('formlabel', {
                                                'is-invalid': form.touched.street_address_2 && form.errors.street_address_2,
                                                'form-label': true
                                            })}
                                                   htmlFor='street_address_2'
                                            >
                                                Street Address 2
                                            </Label>
                                            <Input placeholder="Street Address 2"
                                                   type="text"
                                                   name='street_address_2'
                                                   id='street_address_2'
                                                   value={form.values.street_address_2}
                                                   onBlur={form.handleBlur}
                                                   onChange={form.handleChange}
                                                   className={classnames({'is-invalid': form.touched.street_address_2 && form.errors.street_address_2})}
                                                   disabled={isFormDisabled}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md="6">
                                        <FormGroup>
                                            <Label className={classnames('formlabel', {
                                                'is-invalid': form.touched.city && form.errors.city,
                                                'form-label': true
                                            })}
                                                   htmlFor='city'
                                            >
                                                City <span className="is-invalid ml-25">*</span>
                                            </Label>
                                            <Input placeholder="City"
                                                   type="text"
                                                   name='city'
                                                   id='city'
                                                   value={form.values.city}
                                                   onBlur={form.handleBlur}
                                                   onChange={form.handleChange}
                                                   className={classnames({'is-invalid': form.touched.city && form.errors.city})}
                                                   disabled={isFormDisabled}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md="6">
                                        <FormGroup>
                                            <Label className={classnames('formlabel', {
                                                'is-invalid': form.touched.state && form.errors.state,
                                                'form-label': true
                                            })}
                                                   htmlFor='state'
                                            >
                                                State <span className="is-invalid ml-25">*</span>
                                            </Label>
                                            <Input placeholder="State"
                                                   type="text"
                                                   name='state'
                                                   id='state'
                                                   value={form.values.state}
                                                   onBlur={form.handleBlur}
                                                   onChange={form.handleChange}
                                                   className={classnames({'is-invalid': form.touched.state && form.errors.state})}
                                                   disabled={isFormDisabled}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md="12">
                                        <FormGroup>
                                            <Label className={classnames('formlabel', {
                                                'is-invalid': form.touched.zipcode && form.errors.zipcode,
                                                'form-label': true
                                            })}
                                                   htmlFor='zipcode'
                                            >
                                                Zip Code <span className="is-invalid ml-25">*</span>
                                            </Label>
                                            <Input placeholder="Zip Code"
                                                   type="text"
                                                   name='zipcode'
                                                   id='zipcode'
                                                   value={form.values.zipcode}
                                                   onBlur={form.handleBlur}
                                                   onChange={form.handleChange}
                                                   className={classnames({'is-invalid': form.touched.zipcode && form.errors.zipcode})}
                                                   disabled={isFormDisabled}
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>
                            </CardBody>
                        </Card>
                    )}

                    {data?.campaign_type?.id === 2 && data?.ship_to_canada_po_boxes_ap_boxes !== 1 && (
                        <Card>
                            <CardBody>
                                <p className="font-inter inter-medium m-0">
                                    <i>Please note we cannot ship to Canada.</i>
                                </p>
                            </CardBody>
                        </Card>
                    )}
                </Form>
            )}

            {!data?.is_full && !data?.is_disabled && data?.status !== 'disabled' && (
                <Card>
                    <CardBody>
                        <div className="d-flex align-items-center justify-content-between">
                            {mode === 'update' ? (
                                <Link to="/dashboard/rd/campaigns" className="btn btn-outline-secondary">
                                    Cancel
                                </Link>
                            ) : (
                                <div />
                            )}
                            <Button color="primary" type='button' onClick={() => form.submitForm()} disabled={isFormDisabled}>
                                {applyOnCampaignLoading ? 'Please wait...' : data?.is_expired ? 'Expired' : mode === 'update' ? 'Update Application' : 'Submit'}
                            </Button>
                        </div>
                    </CardBody>
                </Card>
            )}
        </>
    )
}

export default ApplyOnCampaign
